import { useApolloClient } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { COMPUTE_FIELD } from "utils/api/graphql/queries/field-registry";
import { withPartnerCode } from "./computeFieldArgs";
import {
  currentPriceCacheScope,
  registerScopedPriceCache,
  syncPriceCacheScope,
} from "./priceCacheScope";

const computedFieldCache = registerScopedPriceCache(new Map());

export const peopleCacheFingerprint = (project) => {
  const fields = (project && project.fields) || {};
  const related =
    (project && project.contact && project.contact.relatedPersons) || [];
  return JSON.stringify([
    fields.beneficiariesCount ?? "",
    fields.spouseIncluded ?? "",
    related.map((person) => {
      const user =
        person && person.identity && person.identity.user
          ? person.identity.user
          : person || {};
      return [
        person && person.id,
        person && person.relationship,
        user.firstname || "",
        user.lastname || "",
        user.birthDate || "",
      ];
    }),
  ]);
};

export const computedCacheKey = (
  projectId,
  selectedPlanId,
  partnerCode,
  keys,
  paymentStatus,
  quotedPremium,
  peopleFingerprint,
  scopeKey,
) =>
  JSON.stringify([
    projectId,
    selectedPlanId || "",
    partnerCode || "",
    keys,
    paymentStatus || "",
    quotedPremium || "",
    peopleFingerprint || "",
    scopeKey || "",
  ]);

export const useComputedFields = (project, registry, scopeKey = "") => {
  const client = useApolloClient();
  const [computed, setComputed] = useState({});
  const ref = useRef({});
  const scopeRef = useRef(null);

  const projectId = project && project.id;
  const selectedPlanId =
    project && project.fields && project.fields.selectedPlan;
  const partnerCode = project && project.fields && project.fields.partnerCode;
  const paymentStatus =
    project && project.fields && project.fields.paymentStatus;
  const quotedPremium =
    project && project.fields && project.fields.quotedPremium;
  const peopleFingerprint = peopleCacheFingerprint(project);

  useEffect(() => {
    syncPriceCacheScope(scopeKey);
    if (scopeRef.current !== null && scopeRef.current !== scopeKey) {
      setComputed({});
    }
    scopeRef.current = scopeKey;
    if (!projectId || !registry || !registry.length) return;
    const computedEntry = registry.find((g) => g && g.key === "computed");
    const fields = (computedEntry && computedEntry.fields) || [];
    const computeKeys = [];
    for (const f of fields) {
      const key = f && f.target && f.target.compute;
      if (key && computeKeys.indexOf(key) < 0) computeKeys.push(key);
    }
    if (computeKeys.length === 0) return;

    const cacheKey = computedCacheKey(
      projectId,
      selectedPlanId,
      partnerCode,
      computeKeys,
      paymentStatus,
      quotedPremium,
      peopleFingerprint,
      scopeKey,
    );
    const cached = computedFieldCache.get(cacheKey);
    if (cached) {
      setComputed(cached);
      return;
    }

    let cancelled = false;
    const run = async () => {
      const merged = {};
      await Promise.all(
        computeKeys.map(async (key) => {
          try {
            const { data: result } = await client.query({
              query: COMPUTE_FIELD,
              variables: {
                project: { id: projectId },
                key,
                args: withPartnerCode(
                  selectedPlanId ? { insurancePlanId: selectedPlanId } : {},
                  partnerCode,
                ),
              },
              fetchPolicy: "network-only",
            });
            const value = result && result.computeField;
            if (value == null) return;
            if (typeof value === "object" && !Array.isArray(value)) {
              Object.assign(merged, value);
            } else {
              merged[key] = value;
            }
          } catch (_) {}
        }),
      );
      if (!cancelled && currentPriceCacheScope() === scopeKey) {
        computedFieldCache.set(cacheKey, merged);
        setComputed(merged);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    selectedPlanId,
    partnerCode,
    paymentStatus,
    quotedPremium,
    peopleFingerprint,
    registry.length,
    scopeKey,
    client,
  ]);

  ref.current = computed;
  return { computed, computedRef: ref };
};
